import React from 'react'
import { logger } from '../logging'
import { Card, Flex, Grid, SliderField, Text, Divider, Loader, SelectField, TextField, ToggleButtonGroup, ToggleButton, Alert, View, Placeholder } from '@aws-amplify/ui-react'
import { DNS_RESOLVERS, VpnConfig, VpnConfigLimits } from '@mytmpvpn/mytmpvpn-common/models/vpnConfig'
import { InfrastructureProvider, InstanceType } from '@mytmpvpn/mytmpvpn-common/models/location'
import { HelpButton, MainTitle, convertSecondsToHMSLong, convertSecondsToHMSShort, formatMaxPeanuts, formatMaxPeanutsBalance, getBandwidthForInstanceType, getBandwidthLabelForInstanceType, getLabelForInstanceType } from '../components/Common'
import { useComponentSize } from '../components/ResponsiveSize'


/** Skeleton placeholder shown while configuration data is loading */
export function ConfigurationSkeleton() {
    return (
        <Card>
            <MainTitle>Configuration</MainTitle>
            <Flex direction="column" gap="1.5rem" maxWidth="60rem" margin="0 auto" padding="1rem">
                <Placeholder height="2rem" />
                <Placeholder height="4rem" />
                <Placeholder height="4rem" />
                <Flex gap="1rem">
                    <Placeholder width="50%" height="3rem" />
                    <Placeholder width="50%" height="3rem" />
                </Flex>
            </Flex>
        </Card>
    )
}


export function Configuration({ balance, vpnConfigLimits, vpnConfig, setVpnConfig }: {
    balance: number
    vpnConfigLimits: VpnConfigLimits
    vpnConfig: VpnConfig,
    setVpnConfig: any,
}) {
    const componentSize = useComponentSize()

    logger.log(`Adding configuration for: ${JSON.stringify(vpnConfigLimits)}`)
    return (
        <Card>
            <MainTitle>Configuration</MainTitle>
            <Card>
                <Text fontSize="small" color="font.secondary" textAlign="center">
                    All VPNs are temporary. They auto-delete when you remove them manually, after a set duration, or once a peanut limit is reached.
                </Text>
            </Card>
            {componentSize === 'short' ? (
                /* Short (mobile) mode: single column stack */
                <Flex direction="column" gap="1.5em" maxWidth="60rem" margin="0 auto">
                    <View>
                        {vpnConfigLimits ?
                            <DeleteAfterConfiguration
                                vpnConfigLimits={vpnConfigLimits}
                                vpnConfig={vpnConfig}
                                setVpnConfig={setVpnConfig}
                            /> :
                            <Loader size='large' variation={'linear'} />
                        }
                    </View>
                    <View>
                        {vpnConfigLimits ?
                            <MaxPeanutsConfiguration
                                vpnConfigLimits={vpnConfigLimits}
                                balance={balance}
                                vpnConfig={vpnConfig}
                                setVpnConfig={setVpnConfig}
                            /> :
                            <Loader size='large' variation={'linear'} />
                        }
                    </View>
                    <View>
                        <InstanceTypeConfiguration
                            vpnConfig={vpnConfig}
                            setVpnConfig={setVpnConfig}
                        />
                    </View>
                    <Flex direction="row" gap="1em" wrap="wrap">
                        <View flex="1" minWidth="8rem">
                            <PortConfiguration
                                vpnConfigLimits={vpnConfigLimits}
                                vpnConfig={vpnConfig}
                                setVpnConfig={setVpnConfig}
                            />
                        </View>
                        <View flex="1" minWidth="8rem">
                            <DnsConfiguration
                                vpnConfigLimits={vpnConfigLimits}
                                vpnConfig={vpnConfig}
                                setVpnConfig={setVpnConfig}
                            />
                        </View>
                    </Flex>
                </Flex>
            ) : (
                /* Standard (desktop) mode: 3-column grid */
                <Grid templateColumns="2fr 1fr 1fr" gap="1.5em" alignItems="start" maxWidth="60rem" margin="0 auto">
                    {/* Row 1, Col 1 */}
                    <View columnStart="1" columnEnd="2">
                        {vpnConfigLimits ?
                            <DeleteAfterConfiguration
                                vpnConfigLimits={vpnConfigLimits}
                                vpnConfig={vpnConfig}
                                setVpnConfig={setVpnConfig}
                            /> :
                            <Loader size='large' variation={'linear'} />
                        }
                    </View>
                    {/* Row 1, Col 2-3 */}
                    <View columnStart="2" columnEnd="-1">
                        {vpnConfigLimits ?
                            <MaxPeanutsConfiguration
                                vpnConfigLimits={vpnConfigLimits}
                                balance={balance}
                                vpnConfig={vpnConfig}
                                setVpnConfig={setVpnConfig}
                            /> :
                            <Loader size='large' variation={'linear'} />
                        }
                    </View>
                    {/* Row 2, Col 1 */}
                    <View columnStart="1" columnEnd="2">
                        <InstanceTypeConfiguration
                            vpnConfig={vpnConfig}
                            setVpnConfig={setVpnConfig}
                        />
                    </View>
                    {/* Row 2, Col 2-3: Port + DNS side by side */}
                    <View columnStart="2" columnEnd="-1">
                        <Flex direction="row" gap="1.5em" alignItems="flex-start">
                            <View flex="1">
                                <PortConfiguration
                                    vpnConfigLimits={vpnConfigLimits}
                                    vpnConfig={vpnConfig}
                                    setVpnConfig={setVpnConfig}
                                />
                            </View>
                            <View flex="1">
                                <DnsConfiguration
                                    vpnConfigLimits={vpnConfigLimits}
                                    vpnConfig={vpnConfig}
                                    setVpnConfig={setVpnConfig}
                                />
                            </View>
                        </Flex>
                    </View>
                </Grid>
            )}
        </Card >
    )
}

/**
 * Shared layout for all configuration fields.
 * Structure: control (with title) → summary + help button → expanded help text.
 */
function ConfigField({ summary, helpTitle, helpDetail, maxWidth, children }: {
    summary: React.ReactNode
    helpTitle: string
    helpDetail: React.ReactNode
    maxWidth?: string
    children: React.ReactNode
}) {
    const [help, setHelp] = React.useState<boolean>(false)

    return <Flex direction="column" maxWidth={maxWidth} gap="0.5rem">
        {children}
        <Flex alignItems="center" gap="1rem">
            {help
                ? <Text fontSize="0.75rem"><b>{helpTitle}</b><br />{helpDetail}</Text>
                : <Text fontSize="0.75rem">{summary}</Text>
            }
            <HelpButton help={help} setHelp={setHelp}/>
        </Flex>
        <Divider />
    </Flex>
}

function ConfigFieldTitle({ children }: { children: React.ReactNode }) {
    return <Text fontWeight="bold">{children}</Text>
}


function DeleteAfterConfiguration({ vpnConfigLimits, vpnConfig, setVpnConfig }: {
    vpnConfigLimits: VpnConfigLimits
    vpnConfig: VpnConfig
    setVpnConfig: any
}) {
    const componentSize = useComponentSize()
    const label = componentSize === 'short' ? 'Max Time' : 'Maximum Time Allowed'

    return <ConfigField
        summary={<>Auto-deletes after: {new Date(Date.now() + vpnConfig.deleteAfter! * 1000).toLocaleString()}</>}
        helpTitle="VPN auto-deletes after a set duration."
        helpDetail={<>Created now → deleted at {new Date(Date.now() + vpnConfig.deleteAfter! * 1000).toLocaleString()}</>}
    >
        <SliderField
            id='deleteAfter'
            label={<ConfigFieldTitle>{label}</ConfigFieldTitle>}
            defaultValue={vpnConfig.deleteAfter}
            min={vpnConfigLimits.deleteAfterFieldMinValue}
            max={vpnConfigLimits.deleteAfterFieldMaxValue}
            step={vpnConfigLimits.deleteAfterFieldMinValue >= 5 * 60 ? 60 : 1}
            onChange={(value: number) => setVpnConfig({
                ...vpnConfig,
                deleteAfter: value
            } as VpnConfig)}
            formatValue={componentSize === 'short' ? convertSecondsToHMSShort : convertSecondsToHMSLong}
        />
    </ConfigField>
}


function MaxPeanutsConfiguration({ vpnConfigLimits, vpnConfig, setVpnConfig, balance }:
    {
        vpnConfigLimits: VpnConfigLimits
        vpnConfig: VpnConfig
        setVpnConfig: any
        balance: number
    }) {
    const componentSize = useComponentSize()
    const label = componentSize === 'short' ? 'Max 🥜' : 'Maximum Peanuts Allowed'
    const isUnlimited = vpnConfig.maxPeanuts <= 0

    return <ConfigField
        summary={isUnlimited
            ? <>Unlimited: uses all peanuts in your account ({balance.toFixed(2)} available)</>
            : <>Estimated new balance: {formatMaxPeanutsBalance(vpnConfig.maxPeanuts, balance, vpnConfigLimits.maxPeanutsFieldMaxValue)} peanuts</>}
        helpTitle="VPN consumes peanuts while running."
        helpDetail={isUnlimited
            ? <>Set to 0 (unlimited): the VPN will keep running until your entire balance is consumed. No per-VPN cap.</>
            : <>Auto-deletes after {formatMaxPeanuts(vpnConfig.maxPeanuts, balance, vpnConfigLimits.maxPeanutsFieldMaxValue)} consumed, leaving ~{formatMaxPeanutsBalance(vpnConfig.maxPeanuts, balance, vpnConfigLimits.maxPeanutsFieldMaxValue)} peanuts.</>}
    >
        <SliderField
            id='maxPeanuts'
            label={<ConfigFieldTitle>{label}</ConfigFieldTitle>}
            min={0}
            max={Math.min(vpnConfigLimits.maxPeanutsFieldMaxValue, balance)}
            step={1}
            defaultValue={vpnConfig.maxPeanuts}
            onChange={(value: number) => setVpnConfig({
                ...vpnConfig,
                maxPeanuts: value
            } as VpnConfig)}
            formatValue={(value: number) => formatMaxPeanuts(
                value,
                balance,
                vpnConfigLimits.maxPeanutsFieldMaxValue)}
        />
    </ConfigField>
}


function PortConfiguration({ vpnConfigLimits, vpnConfig, setVpnConfig }: {
    vpnConfigLimits: VpnConfigLimits
    vpnConfig: VpnConfig
    setVpnConfig: any
}) {
    const [invalidPort, setInvalidPort] = React.useState<boolean>(false)
    const validatePort = (e: any) => {
        try {
            const port = Number.parseInt(e.currentTarget.value)
            if (isNaN(port) || port < 0 || port > 65535) {
                setInvalidPort(true)
                return
            }
            setInvalidPort(false)
            setVpnConfig({ ...vpnConfig, port } as VpnConfig)
        } catch (err) {
            setInvalidPort(true)
        }
    }

    return <ConfigField
        summary={vpnConfig.port === 0 ? 'Random' : vpnConfig.port}
        helpTitle="VPN listening port (0 = random)."
        helpDetail="Some firewalls block uncommon ports. Try 53, 443, or 80 if you have trouble connecting."
    >
        <TextField
            placeholder={`${vpnConfig.port}`}
            errorMessage="0–65535"
            onChange={validatePort}
            hasError={invalidPort}
            label={<ConfigFieldTitle>Port</ConfigFieldTitle>}
            maxLength={5}
        />
    </ConfigField>
}


function DnsConfiguration({ vpnConfigLimits, vpnConfig, setVpnConfig }: {
    vpnConfigLimits: VpnConfigLimits
    vpnConfig: VpnConfig
    setVpnConfig: any
}) {
    return <ConfigField
        summary={vpnConfig.dns === undefined ? 'Random' : DNS_RESOLVERS[vpnConfig.dns]?.label ?? vpnConfig.dns}
        helpTitle="DNS resolver for your VPN."
        helpDetail="Random picks one for you."
    >
        <SelectField
            id='dns'
            label={<ConfigFieldTitle>DNS</ConfigFieldTitle>}
            value={vpnConfig.dns ?? ''}
            onChange={(e) =>
                setVpnConfig({
                    ...vpnConfig,
                    dns: e.target.value === '' ? undefined : e.target.value
                } as VpnConfig)}
        >
            <option key="random" value="">Random</option>
            {Object.entries(DNS_RESOLVERS).map(([key, info]) => (
                <option key={key} value={key}>{info.label}</option>
            ))}
        </SelectField>
    </ConfigField>
}

function getUsageForInstanceType(instanceType: InstanceType): React.ReactNode {
    switch (instanceType) {
        case InstanceType.Ultra:
            return `Top speed, up to 10 users`
        case InstanceType.Fast:
            return `Ideal for 4k video streaming`
        case InstanceType.Eco:
            return `Ideal for HD video streaming, web browsing or chat`
        default:
            return <Alert>Unknown Instance Type</Alert>
    }
}


export function InstanceTypeConfiguration({ vpnConfig, setVpnConfig }: {
    vpnConfig: VpnConfig
    setVpnConfig: any
}) {
    const instanceType = vpnConfig.instanceType ?? InstanceType.Fast

    return <ConfigField
        summary={getBandwidthLabelForInstanceType(InfrastructureProvider.Aws, instanceType)}
        helpTitle="Higher profile = more bandwidth, more peanuts/min."
        helpDetail={<>{getLabelForInstanceType(instanceType)}: up to {`${getBandwidthForInstanceType(InfrastructureProvider.Aws, instanceType)}`} Mb/s. {getUsageForInstanceType(instanceType)}</>}
    >
        <ConfigFieldTitle>Profile</ConfigFieldTitle>
        <ToggleButtonGroup
            id="instanceType"
            value={instanceType}
            onChange={(value) => setVpnConfig({ ...vpnConfig, instanceType: value })}
            isExclusive={true}
            isSelectionRequired={true}
        >
            {Object.values(InstanceType).map(it => (
                <ToggleButton key={it} value={it}>
                    {getLabelForInstanceType(it)}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    </ConfigField>
}
